import { CubejsApi, DeeplyReadonly, Query, QueryRecordType, ResultSet } from '@cubejs-client/core';
import Axios from '../AxiosConfig/axiosConfigSAT';
import { hasInvalidCubePayload, transformCubeFilters } from './cubeUtil';
import { env } from '../../env';
/**
* This function makes a Cube API call and tracks the call duration along with the success/error response.
*
* @param cubeJsApi - The CubejsApi instance.
* @param query - The query to be executed.
* @param isMultiQuery - (Optional) A flag to determine whether it's a multi-query request.
* @returns A Promise with the result of the query, including success/error flags and the data.
*/
interface FetchAndTrackResponse {
 isSuccess: boolean;
 isError: boolean;
 data: any;
}
const fetchAndTrackCubeQuery = async (
 cubeJsApi: CubejsApi,
 query: Query | Query[],
 isMultiQuery = false
): Promise<FetchAndTrackResponse> => {
  // cube query validation
  if(hasInvalidCubePayload(query)){
    return {
      isSuccess: false,
      isError: true,
      data: []
    }
  }
  // Logger Enabled Functionality
  const loggerSeed = localStorage.getItem("loggerSeed") && JSON.parse(localStorage.getItem("loggerSeed") as any);
  const isMetricEnabled = loggerSeed && loggerSeed.find((val:any) => val["key"] === "cubeMetric")?.value === 'true'
  const isEventEnabled = loggerSeed && loggerSeed.find((val:any) => val["key"] === "cubeEvent")?.value === 'true'

  const startTime = Date.now();
  let transformedQuery: Query | Query[] = query;
  try {
      if (Array.isArray(query)) {
        transformedQuery = query.map(val => ({
          ...val,
          filters: transformCubeFilters(val?.filters || [])
        }));
      } else {
        transformedQuery = {
          ...query,
          filters: transformCubeFilters(query?.filters || [])
        };
      }

      const resultSet: ResultSet<DeeplyReadonly<Query | Query[]>> = await cubeJsApi.load(transformedQuery);
      const endTime = Date.now();
      const duration = endTime - startTime;

      // Success Metric Callback
      if(isMetricEnabled){
        const metricPayload = {
          type:"Metric",
          payload:{
            name:"useCubeQuery Response Time",
            url: window.location.href,
            query: transformedQuery,
            duration
          }
        }
        await Axios.post(`${env.azure_Insights_Logger_CubeCalls_Tracking}/AzureInsightsLogger`, metricPayload)
      }

      // Success Event Callback
      if(isEventEnabled){
        const eventPayload = {
            type:"Event",
            payload:{
              name:"useCubeQuery Success",
              url:window.location.href,
              query: transformedQuery,
              resultSet
            }
          }
        await Axios.post(`${env.azure_Insights_Logger_CubeCalls_Tracking}/AzureInsightsLogger`, eventPayload);
      }

      return {
        isSuccess: true,
        isError: false,
        data: isMultiQuery
          ? resultSet
          : resultSet.serialize().loadResponse.results.map((ele) => ele.data),
      };
    } catch (error) {
      
      // Error Event Callback
      if(isEventEnabled){
        const eventPayload = {
          type:"Event",
          payload:{
            name:"useCubeQuery Error",
            url:window.location.href,
            query: transformedQuery,
            error
          }
        }
        await Axios.post(`${env.azure_Insights_Logger_CubeCalls_Tracking}/AzureInsightsLogger`, eventPayload)
      }

      return {
        isSuccess: false,
        isError: true,
        data: error instanceof Error ? error.message: 'Unknown cube error',
      };
    }
};

export default fetchAndTrackCubeQuery;